import type { ItemCollectionResponse, OrderDirection } from '../types/api';
import type {
    FlowUsageDashboardResponse,
    StateError,
    StateErrorsLineChartData,
    StateErrorsPieChartData,
    TimeFilters,
} from '../types/dashboard';
import type { StateResponseAPI } from '../types/states';
import { fetchAndParse } from '../utils/ajax';
import { getTenantId } from '../utils/tenant';

export type Status = 'in_progress' | 'done' | 'expired' | 'errored';

export const getStatesByFlow = (
    flowId: string,
    params: {
        status: Status;
        orderBy: string;
        orderDirection: OrderDirection;
        pageSize: number;
        page: number;
        from: string;
        to: string;
    },
) => {
    const URLparams = new URLSearchParams();

    URLparams.append('status', params.status);
    URLparams.append('pageSize', params.pageSize.toString());
    URLparams.append('page', params.page.toString());
    if (params.orderBy) {
        URLparams.append('orderBy', params.orderBy);
    }
    if (params.orderDirection) {
        URLparams.append('orderDirection', params.orderDirection);
    }
    if (params.from) {
        URLparams.append('from', params.from);
    }
    if (params.to) {
        URLparams.append('to', params.to);
    }

    const url = `/api/admin/1/states/flow/${flowId}?${URLparams.toString()}`;

    return fetchAndParse<ItemCollectionResponse<StateResponseAPI>>({
        url,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });
};

export const getUsageForTenant = (interval: TimeFilters, toDate: Date) => {
    const URLparams = new URLSearchParams();
    URLparams.append('interval', interval);
    URLparams.append('toDate', toDate.toISOString());

    const url = `/api/dashboard/1/flows?${URLparams.toString()}`;
    return fetchAndParse<FlowUsageDashboardResponse[]>({
        url,
        method: 'GET',
        headers: { ManyWhoTenant: getTenantId() },
    });
};

export const getStateErrorsForTable = (
    interval: TimeFilters,
    toDate: Date,
    page: number,
    searchFilter: string,
    flowNameFilter: string,
) => {
    const URLparams = new URLSearchParams();
    URLparams.append('interval', interval);
    URLparams.append('toDate', toDate.toISOString());
    URLparams.append('page', page.toString());
    URLparams.append('searchFilter', searchFilter);
    URLparams.append('flowNameFilter', flowNameFilter);

    const url = `/api/dashboard/1/stateErrors?${URLparams.toString()}`;
    return fetchAndParse<ItemCollectionResponse<StateError>>({
        url,
        method: 'GET',
        headers: { ManyWhoTenant: getTenantId() },
    });
};

export const getStateErrorsForLineChart = (
    interval: TimeFilters,
    toDate: Date,
    searchFilter: string,
    flowNameFilter: string,
) => {
    const URLparams = new URLSearchParams();
    URLparams.append('interval', interval);
    URLparams.append('toDate', toDate.toISOString());
    URLparams.append('searchFilter', searchFilter);
    URLparams.append('flowNameFilter', flowNameFilter);

    const url = `/api/dashboard/1/stateErrorsLineChart?${URLparams.toString()}`;
    return fetchAndParse<StateErrorsLineChartData>({
        url,
        method: 'GET',
        headers: { ManyWhoTenant: getTenantId() },
    });
};

export const getStateErrorsForPieChart = (
    interval: TimeFilters,
    toDate: Date,
    searchFilter: string,
    flowNameFilter: string,
) => {
    const URLparams = new URLSearchParams();
    URLparams.append('interval', interval);
    URLparams.append('toDate', toDate.toISOString());
    URLparams.append('searchFilter', searchFilter);
    URLparams.append('flowNameFilter', flowNameFilter);

    const url = `/api/dashboard/1/stateErrorsPieChart?${URLparams.toString()}`;
    return fetchAndParse<StateErrorsPieChartData>({
        url,
        method: 'GET',
        headers: { ManyWhoTenant: getTenantId() },
    });
};
